import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
// Importation de PropTypes pour la validation des props
import { Box, useTheme } from '@mui/material';
import './Gantt.css';
import { useGantt } from './GanttHook';

const CustomTooltipContent = ({ task, fontSize, fontFamily }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const title = task?.name ?? '';
  let durationDays = '';
  if (task?.start && task?.end) {
    const ms = task.end.getTime() - task.start.getTime();
    durationDays = Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        padding: '10px 12px',
        fontSize: fontSize || '12px',
        fontFamily: fontFamily || 'Arial, sans-serif',
        boxShadow: theme.shadows[3],
      }}
    >
      <div style={{ margin: 0, fontWeight: 700 }}>
        {title}
        {task?.start && task?.end && (
          <>
            : {task.start.toLocaleDateString()} - {task.end.toLocaleDateString()}
          </>
        )}
      </div>
      {durationDays !== '' && (
        <div style={{ marginTop: 8, color: theme.palette.text.secondary }}>
          {t('genericcomponent.technicalPolicy.gantt.duration', 'Duration')}: {durationDays}{' '}
          {t('genericcomponent.technicalPolicy.gantt.days', 'day(s)')}
        </div>
      )}
      {task?.typePriorisation && (
        <div style={{ marginTop: 4, color: theme.palette.text.secondary }}>
          {t('genericcomponent.technicalPolicy.gantt.type', 'Type')}: {task.typePriorisation}
        </div>
      )}
      {task?.sequenceActions1 && (
        <div style={{ marginTop: 4, color: theme.palette.text.secondary }}>
          {t('genericcomponent.technicalPolicy.gantt.actionSequence1', 'Action Sequence 1')}: {task.sequenceActions1}
        </div>
      )}
      {task?.sequenceActions2 && (
        <div style={{ marginTop: 4, color: theme.palette.text.secondary }}>
          {t('genericcomponent.technicalPolicy.gantt.actionSequence2', 'Action Sequence 2')}: {task.sequenceActions2}
        </div>
      )}
      {task?.niveauDePriorisation && (
        <div style={{ marginTop: 4, color: theme.palette.text.secondary }}>
          {t('genericcomponent.technicalPolicy.gantt.prioritizationLevel', 'Prioritization Level')}:{' '}
          {task.niveauDePriorisation}
        </div>
      )}
    </div>
  );
};

CustomTooltipContent.propTypes = {
  task: PropTypes.shape({
    name: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
    typePriorisation: PropTypes.string,
    niveauDePriorisation: PropTypes.string,
    sequenceActions1: PropTypes.string,
    sequenceActions2: PropTypes.string,
  }),
  fontSize: PropTypes.string,
  fontFamily: PropTypes.string,
};

// Personnalisation de l'en-tête de la liste des tâches
const CustomTaskListHeader = () => {
  const { t } = useTranslation();
  return (
    <div className="ganttTable_Header ganttTable_Header_custom">
      <div style={{ width: '200px' }}>
        {t('genericcomponent.technicalPolicy.common.assetClusters', 'Asset Clusters')}
      </div>
    </div>
  );
};

// Personnalisation de la table des tâches
const CustomTaskListTable = ({ rowHeight, tasks, setSelectedTask, handleGisementClick, onPolicyLabelClick }) => {
  const gisementIdToName = {};
  tasks.forEach((task) => {
    if (task.type === 'project') {
      gisementIdToName[task.id] = task.name;
    }
  });

  return (
    <div className="ganttTable_TBody">
      {tasks.map((task) => (
        <div
          data-testid={`${task.type}-${task.name}`}
          data-cy={task.type === 'task' ? `policy-rule-chip-${task.name}` : undefined}
          key={task.id}
          className={`ganttTable_TBody-row ${task.type === 'project' ? 'gisement-row' : 'regle-row'}`}
          style={{
            height: rowHeight,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '10px',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (task.type === 'task') {
              e.currentTarget.style.backgroundColor = 'rgba(66, 133, 244, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            if (task.type === 'task') {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (task.type === 'project') {
              handleGisementClick(task.id);
            } else {
              setSelectedTask(task.id);
              const parentGisementName = gisementIdToName[task.project];
              if (onPolicyLabelClick) onPolicyLabelClick(parentGisementName, task.policyName);
            }
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            {task.type === 'project' && (
              <span className={`expander ${task.hideChildren ? 'closed' : 'open'}`} style={{ marginRight: '10px' }}>
                {task.hideChildren ? '▶' : '▼'}
              </span>
            )}
            <span style={{ fontWeight: task.type === 'task' ? 'normal' : 'bold' }}>{task.name}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

CustomTaskListTable.propTypes = {
  rowHeight: PropTypes.number.isRequired, // Hauteur de la ligne, attendu en nombre
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired, // L'ID de la tâche doit être une chaîne
      name: PropTypes.string.isRequired, // Le nom de la tâche
      type: PropTypes.string.isRequired, // Le type de la tâche (project ou regle)
      hideChildren: PropTypes.bool, // Optionnel : booléen pour indiquer si les enfants sont cachés
    })
  ).isRequired,
  setSelectedTask: PropTypes.func.isRequired, // Fonction pour définir la tâche sélectionnée
  handleGisementClick: PropTypes.func.isRequired, // Fonction pour gérer le clic sur les gisements
  onPolicyLabelClick: PropTypes.func,
};

const ReglesGestionGantt = ({ onPolicyLabelClick }) => {
  const [viewMode] = useState(ViewMode.Year); // Mode de vue par défaut (année)
  const { tasks: tasksData } = useGantt();
  const [tasks, setTasks] = useState(tasksData); // Initialisation des tâches à partir d'un helper
  const [isChecked] = useState(true); // Contrôle si certaines options (comme les colonnes) sont affichées
  const columnWidth = 100; // Largeur des colonnes du Gantt
  // Mise à jour des tâches quand une expander (flèche collapse/expand) est cliquée
  const handleExpanderClick = (task) => {
    setTasks(tasks.map((t) => (t.id === task.id ? task : t)));
  };

  // Gérer le collapse/expand des gisements
  const handleGisementClick = (gisementId) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) => {
        if (task.id === gisementId) {
          return { ...task, hideChildren: !task.hideChildren }; // Inverser l'état de hideChildren
        }
        return task;
      })
    );
  };

  // Update tasks when tasksData changes to rerender gantt chart
  // on start or end date change for example
  useEffect(() => {
    setTasks(tasksData);
  }, [tasksData]);

  return (
    <Box data-cy="gantt-container" sx={{ padding: '20px' }} className="gantt-container">
      {tasks && (
        <Gantt
          tasks={tasks}
          viewMode={viewMode}
          onExpanderClick={handleExpanderClick}
          arrowColor={'transparent'} // Cacher les flèches par défaut entre les tâches
          listCellWidth={isChecked ? '155px' : ''} // Largeur conditionnelle des cellules de liste
          columnWidth={columnWidth} // Largeur des colonnes du diagramme Gantt
          TaskListHeader={CustomTaskListHeader} // Utilisation de l'en-tête personnalisé
          TaskListTable={(props) => {
            return (
              <CustomTaskListTable
                {...props}
                handleGisementClick={handleGisementClick}
                onPolicyLabelClick={onPolicyLabelClick}
              />
            );
          }} // Utilisation de la table personnalisée avec gestion du collapse/expand
          TooltipContent={CustomTooltipContent}
        />
      )}
    </Box>
  );
};

ReglesGestionGantt.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired, // ID unique pour chaque tâche
      name: PropTypes.string.isRequired, // Nom de la tâche
      start: PropTypes.instanceOf(Date).isRequired, // Date de début de la tâche
      end: PropTypes.instanceOf(Date).isRequired, // Date de fin de la tâche
      type: PropTypes.string.isRequired, // Type de la tâche (project, task, etc.)
      hideChildren: PropTypes.bool, // Optionnel : cacher les enfants ou non
    })
  ),
  viewMode: PropTypes.oneOf(Object.values(ViewMode)), // Mode de vue du Gantt (ex: Year, Month)
  onPolicyLabelClick: PropTypes.func,
};

export default ReglesGestionGantt;
